import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { environment } from 'src/environments/environment'
import { catchError } from 'rxjs/operators'
import { forkJoin, of } from 'rxjs'
import {
    SHIKSHA_DASHBOARD_API_ENDPOINTS,
    SHIKSHA_DASHBOARD_API_PARAMS,
} from '@features/home/shiksha-components/constants/shiksha-dashboard.constants'
import {
    IFinancialYearsResponse,
    ILostS3S4AggregationResponse,
    IRevenueHealthMetricsResponse,
    IS3EstimateDealValueResponse,
    ISalesFunnelSnapshotsResponse,
} from '@features/home/models/shiksha-dashboard.model'

@Injectable({
    providedIn: 'root',
})
export class ShikshaDashboardDataService {
    private readonly http = inject(HttpClient)

    fetchFinancialYears() {
        const endpoint = `${environment.apiBaseUrl}${SHIKSHA_DASHBOARD_API_ENDPOINTS.FINANCIAL_YEARS}`
        return this.http.get<IFinancialYearsResponse>(endpoint).pipe(catchError(() => of(null)))
    }

    fetchRevenueHealthMetrics(financialYear: number, scope: 'my' | 'team', userIds: string | null) {
        const endpoint = `${environment.apiBaseUrl}${SHIKSHA_DASHBOARD_API_ENDPOINTS.REVENUE_HEALTH_METRICS}`
        const salesFunnelEndpoint = `${environment.apiBaseUrl}${SHIKSHA_DASHBOARD_API_ENDPOINTS.SALES_FUNNEL_SNAPSHOTS}`
        const s3EstimateEndpoint = `${environment.apiBaseUrl}${SHIKSHA_DASHBOARD_API_ENDPOINTS.S3_ESTIMATE_DEAL_VALUE}`
        const lostS3S4Endpoint = `${environment.apiBaseUrl}${SHIKSHA_DASHBOARD_API_ENDPOINTS.LOST_S3_S4_AGGREGATION}`

        const kpiParams = this.buildRevenueHealthParams(financialYear, scope, userIds, false)
        const contributionParams = this.buildRevenueHealthParams(
            financialYear,
            scope,
            userIds,
            true,
        )
        const salesFunnelApiParams = this.buildSalesFunnelParams(financialYear, scope, userIds)
        const targetsDealsScopeParams = this.buildTargetsDealsScopeParams(
            financialYear,
            scope,
            userIds,
        )
        const lostDealsAtRiskParams = this.buildLostDealsAtRiskParams(financialYear, scope, userIds)

        return forkJoin({
            kpiResponse: this.http
                .get<IRevenueHealthMetricsResponse>(endpoint, { params: kpiParams })
                .pipe(catchError(() => of(null))),
            contributionResponse: this.http
                .get<IRevenueHealthMetricsResponse>(endpoint, { params: contributionParams })
                .pipe(catchError(() => of(null))),
            salesFunnelResponse: this.http
                .get<ISalesFunnelSnapshotsResponse>(salesFunnelEndpoint, {
                    params: salesFunnelApiParams,
                })
                .pipe(catchError(() => of(null))),
            s3EstimateResponse: this.http
                .get<IS3EstimateDealValueResponse>(s3EstimateEndpoint, {
                    params: targetsDealsScopeParams,
                })
                .pipe(catchError(() => of(null))),
            lostS3S4Response: this.http
                .get<ILostS3S4AggregationResponse>(lostS3S4Endpoint, {
                    params: lostDealsAtRiskParams,
                })
                .pipe(catchError(() => of(null))),
        })
    }

    private buildRevenueHealthParams(
        financialYear: number,
        scope: 'my' | 'team',
        userIds: string | null,
        includeTeamContribution: boolean,
    ): HttpParams {
        let params = new HttpParams()
            .set(SHIKSHA_DASHBOARD_API_PARAMS.FINANCIAL_YEAR, String(financialYear))
            .set(SHIKSHA_DASHBOARD_API_PARAMS.SCOPE, scope)
            .set(
                SHIKSHA_DASHBOARD_API_PARAMS.TEAM_CONTRIBUTION,
                includeTeamContribution ? 'true' : 'false',
            )

        if (userIds) {
            params = params.set(SHIKSHA_DASHBOARD_API_PARAMS.USER_IDS, userIds)
        }

        return params
    }

    private buildSalesFunnelParams(
        financialYear: number,
        scope: 'my' | 'team',
        userIds: string | null,
    ): HttpParams {
        const params = new HttpParams()
            .set(SHIKSHA_DASHBOARD_API_PARAMS.FINANCIAL_YEAR, String(financialYear))
            .set(SHIKSHA_DASHBOARD_API_PARAMS.SCOPE, scope)

        const normalizedUserIds = userIds?.trim()
        if (!normalizedUserIds) {
            return params
        }

        if (normalizedUserIds.includes(',')) {
            return params.set(SHIKSHA_DASHBOARD_API_PARAMS.USER_IDS, normalizedUserIds)
        }

        return params.set(SHIKSHA_DASHBOARD_API_PARAMS.USER_ID, normalizedUserIds)
    }

    /** Shared by targets deal endpoints (`financial_year`, `team_view`, optional `user_id`). */
    private buildTargetsDealsScopeParams(
        financialYear: number,
        scope: 'my' | 'team',
        userIds: string | null,
    ): HttpParams {
        let params = new HttpParams()
            .set(SHIKSHA_DASHBOARD_API_PARAMS.FINANCIAL_YEAR, String(financialYear))
            .set(SHIKSHA_DASHBOARD_API_PARAMS.TEAM_VIEW, scope === 'team' ? 'true' : 'false')

        const normalizedUserIds = userIds?.trim()
        if (!normalizedUserIds) {
            return params
        }

        if (normalizedUserIds.includes(',')) {
            const primaryUserId = normalizedUserIds.split(',')[0]?.trim()
            if (primaryUserId) {
                params = params.set(SHIKSHA_DASHBOARD_API_PARAMS.USER_ID, primaryUserId)
            }
            return params
        }

        return params.set(SHIKSHA_DASHBOARD_API_PARAMS.USER_ID, normalizedUserIds)
    }

    /**
     * Deals-at-risk (lost S3/S4) endpoint requires `scope=team` specifically for Entire Team view.
     * Keep this isolated so other target/deal endpoints retain their existing query shape.
     */
    private buildLostDealsAtRiskParams(
        financialYear: number,
        scope: 'my' | 'team',
        userIds: string | null,
    ): HttpParams {
        let params = this.buildTargetsDealsScopeParams(financialYear, scope, userIds)
        if (scope === 'team') {
            params = params.set(SHIKSHA_DASHBOARD_API_PARAMS.SCOPE, scope)
        }
        return params
    }
}
